/**
 * Reusable admin banner/image upload field.
 *
 * Replaces the "paste a URL" text input with a real upload button that:
 *   - lets the admin pick a photo directly from their phone/computer
 *   - POSTs multipart to /api/upload/image?purpose=banner
 *   - stores the returned hosted URL in the parent form
 *   - shows a live preview + Replace/Remove actions
 *
 * Props:
 *   value        — current image URL (or null/empty)
 *   onChange(v)  — called with the new URL (or "" on Remove)
 *   purpose      — "banner" | "cover" | "beat" | ... (matches server whitelist)
 *   testIdPrefix — prefix for data-testids so multiple instances can coexist
 */
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

export const BannerUploadField = ({
  value,
  onChange,
  purpose = "banner",
  testIdPrefix = "banner-upload",
  label = "Banner image",
}) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";           // reset so same file can be re-selected
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10 MB).");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", purpose);
      const r = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.url;
      if (!url) throw new Error("No URL returned");
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testIdPrefix}>
      <label className="text-[11px] uppercase tracking-widest text-gray-400 font-black block mb-1">
        {label}
      </label>
      {value ? (
        <div className="rounded-lg border border-white/10 bg-black/40 p-2" data-testid={`${testIdPrefix}-preview`}>
          <img
            src={value}
            alt="Banner preview"
            className="w-full max-h-40 object-cover rounded-md"
            onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
          />
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              onClick={pick}
              disabled={busy}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 text-xs h-8"
              data-testid={`${testIdPrefix}-replace`}
            >
              {busy ? "Uploading…" : "Replace"}
            </Button>
            <Button
              type="button"
              onClick={() => onChange("")}
              disabled={busy}
              variant="outline"
              className="border-rose-500/30 text-rose-200 hover:bg-rose-500/10 text-xs h-8"
              data-testid={`${testIdPrefix}-remove`}
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className="w-full h-24 rounded-lg border-2 border-dashed border-white/15 bg-white/[0.02] hover:border-purple-500/50 hover:bg-white/[0.04] transition-colors flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-white disabled:opacity-50"
          data-testid={`${testIdPrefix}-picker`}
        >
          <span className="text-2xl">📷</span>
          <span className="text-xs font-black">{busy ? "Uploading…" : "Tap to upload from device"}</span>
          <span className="text-[10px] text-gray-500">JPG or PNG · up to 10 MB</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={upload}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
    </div>
  );
};

export default BannerUploadField;
